import {
	Configuration,
	LogLevel,
	RedirectRequest,
	PublicClientApplication,
	IPublicClientApplication,
	AccountInfo,
	InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { globalEndpoints } from 'constants/global.constants';

const LOGIN_IN_FLIGHT_KEY = 'LOGIN_IN_FLIGHT';

const {
	REACT_APP_ENV,
	REACT_APP_MSAL_REDIRECT,
	REACT_APP_MSAL_POST_LOGOUT_REDIRECT,
	REACT_APP_ADB2C_AUTHORITY,
	REACT_APP_ADB2C_AUTHORITY_DOMAIN,
	REACT_APP_ADB2C_SIGN_IN_FLOW,
	REACT_APP_ADB2C_CLIENT_ID,
} = globalEndpoints;

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const B2C_POLICIES = {
	names: {
		signUpSignIn: REACT_APP_ADB2C_SIGN_IN_FLOW,
	},
	authorities: {
		signUpSignIn: {
			authority: REACT_APP_ADB2C_AUTHORITY,
		},
	},
	authorityDomain: REACT_APP_ADB2C_AUTHORITY_DOMAIN!,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
	auth: {
		clientId: REACT_APP_ADB2C_CLIENT_ID!, // This is the ONLY mandatory field that you need to supply.
		authority: B2C_POLICIES.authorities.signUpSignIn.authority,
		knownAuthorities: [B2C_POLICIES.authorityDomain], // Mark your B2C tenant's domain as trusted.
		redirectUri: REACT_APP_MSAL_REDIRECT,
		postLogoutRedirectUri: REACT_APP_MSAL_POST_LOGOUT_REDIRECT, // Indicates the page to navigate after logout.
		navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
	},
	cache: {
		cacheLocation: 'localStorage', // This configures where your cache will be stored
		storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
	},
	system: {
		loggerOptions: {
			loggerCallback: (level, message, containsPii) => {
				if (containsPii) {
					return;
				}
				switch (level) {
					case LogLevel.Error:
						// console.error(message)
						return;
					case LogLevel.Info:
						// console.info(message)
						return;
					case LogLevel.Verbose:
						// console.debug(message)
						return;
					case LogLevel.Warning:
						// console.warn(message)
						return;
					case LogLevel.Trace:
						return;
				}
			},
		},
	},
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: RedirectRequest = {
	scopes: ['openid'],
};

export const loginRedirectOnce = async (msalInstance: PublicClientApplication, scopes = loginRequest.scopes) => {
	if (typeof window === 'undefined') {
		return;
	}
	if (sessionStorage.getItem(LOGIN_IN_FLIGHT_KEY)) {
		return;
	}
	sessionStorage.setItem(LOGIN_IN_FLIGHT_KEY, '1');
	await msalInstance.loginRedirect({ scopes, redirectUri: msalConfig.auth.redirectUri });
};

export const getToken = async (msalInstance: PublicClientApplication): Promise<string> => {
	const accounts = msalInstance.getAllAccounts();
	const activeAccount = msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
	const account = activeAccount || (accounts.length === 1 ? accounts[0] : accounts[0] || null);

	if (!activeAccount && account) {
		msalInstance.setActiveAccount(account);
	}

	if (!account) {
		await loginRedirectOnce(msalInstance);
		return '';
	}

	const request = {
		scopes: loginRequest.scopes,
		account,
	};

	try {
		const authResult = await msalInstance.acquireTokenSilent(request);
		return authResult.idToken;
	} catch (error: any) {
		await loginRedirectOnce(msalInstance);
		return '';
	}
};

export const logoutUser = (instance: IPublicClientApplication, accounts: AccountInfo[]) => {
	instance.logoutRedirect({
		account: accounts[0],
		postLogoutRedirectUri: msalConfig.auth.redirectUri,
	});
};
